import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TrackingOrderService } from 'app/services/tracking-order.service';
import { MasterDataService } from 'app/services/admin/master-data.service';
import { format } from 'date-fns';
import { Constants } from 'app/shared/constant';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LocalService } from 'app/services/local.service';
import { CustomerService } from 'app/services/customer.service';
import { AdminUserService } from 'app/services/admin/user.service';

interface ProductAdvertisement {
  title: string;
  rating: number;
  originalPrice: string;
  price: string;
  discount: string;
  imageUrl: string;
  category: string;
  cta: string;
  stockLabel: string;
  url: string;
  badge: string;
  taxonomy_title: string;
  sales: string;
  stock: number;
}

@Component({
  selector: 'app-track-order',
  templateUrl: './track-order.component.html',
  styleUrls: ['./track-order.component.scss']
})
export class TrackOrderComponent implements OnInit {
  @ViewChild('trackingInput', { static: false }) trackingInput: ElementRef;
  @ViewChild('noOrderFile') noOrderFileTemplate!: ElementRef<any>;

  trackingNumber: string;
  orderNumber: string;
  OrderDetails: any;
  trackingDetails: any = [];
  noOrderFound: boolean = false;
  selectedType: string = 'tracking_number';
  placeholder = 'Enter tracking number';
  progressWidth = 0;
  carrierCompanySlug = Constants.carrierCompanySlug;
  actualTrackingDetails: any = [];
  isDisplayTrackingDetails: boolean;
  scrapedProducts: ProductAdvertisement[] = [];
  marqueeProducts: ProductAdvertisement[] = [];
  njourImageUrl: string = '';
  carrierCompanyList = Constants.carrierCompanyList;
  showUpdateTrackingModal = false;
  isUpdatingTracking = false;
  updateTrackingForm: FormGroup;
  canUpdateTracking = false;
  latestTracking: any;

  get hasOrderDetails(): boolean {
    return !!this.OrderDetails && !Array.isArray(this.OrderDetails);
  }

  constructor(
    private _trackingOrderService: TrackingOrderService,
    private _masterDataService: MasterDataService,
    private _activatedRoute: ActivatedRoute,
    private _formBuilder: FormBuilder,
    private toaster: ToastrService,
    private _localService: LocalService,
    private _customerService: CustomerService,
    private _adminUserService: AdminUserService,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {
    console.log('TrackOrderComponent initialized');
    this.updateTrackingForm = this._formBuilder.group({
      shippingBy: ['', Validators.required],
      trackingNumber: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.refreshUpdateTrackingAccess();
    this._customerService.isLogin.subscribe(() => this.refreshUpdateTrackingAccess());
    this._adminUserService.isLogin.subscribe(() => this.refreshUpdateTrackingAccess());
    this.getNjourImageUrl();
    this._activatedRoute.queryParams.subscribe((res: any) => {
      if (res && res.tracking_number) {
        this.getOrderTrackingDetails(res.tracking_number);
      }
    });

    this.scrapeProductData();
  }

  getNjourImageUrl(): void {
    this._masterDataService.getNjourImageUrl().subscribe({
      next: (response) => {
        if (response && response.data && response.data.image_url) {
          this.njourImageUrl = response.data.image_url;
        }
      },
      error: (error) => {
        console.error('Njour Image URL Error:', error);
      }
    });
  }

  scrapeProductData(): void {
    this._masterDataService.scrapeProduct().subscribe({
      next: (response) => {
        if (response && response.data && Array.isArray(response.data)) {
          this.scrapedProducts = response.data
            .map((item: any) => this.mapScrapedProduct(item))
            .filter((product: any): product is ProductAdvertisement => product !== null);
          this.marqueeProducts = this.scrapedProducts.length > 2
            ? [...this.scrapedProducts, ...this.scrapedProducts]
            : [...this.scrapedProducts];
        } else if (response && response.data) {
          const product = this.mapScrapedProduct(response.data);
          this.scrapedProducts = product ? [product] : [];
          this.marqueeProducts = [...this.scrapedProducts];
        }
      },
      error: (error) => {
        console.error('Scrape Product Error:', error);
      }
    });
  }

  getOrderTrackingDetails(value: string, selectedType?: string): void {
    if (value === '') {
      return;
    }
    const searchType = selectedType || this.selectedType;
    this.OrderDetails = null;
    this.actualTrackingDetails = [];
    this.trackingDetails = [];
    this.noOrderFound = false;
    this._trackingOrderService.getTrackingOrderDetails(searchType, value).subscribe({
      next: (response: any) => {
        this.noOrderFound = false;
        this.OrderDetails = response.orderDetails;
        this.trackingDetails = response.trackingData;
        if (response.actualTrackingDetails && response.actualTrackingDetails.status === 200) {
          const { trackingData } = response.actualTrackingDetails;
          this.actualTrackingDetails = trackingData || [];
        }
        this.trackingDetails.sort((a, b) => new Date(b.status_index).getTime() - new Date(a.status_index).getTime());
        this.updateStatus();
        this.updateProgressBarWidth();
        if (this.trackingInput?.nativeElement && !selectedType) {
          value = '';
          this.trackingInput.nativeElement.value = '';
        }
      },
      error: ({ error }: { error: any }) => {
        this.noOrderFound = true;
        // this.toaster.error(error ? error.message : 'Something went wrong.', '');
        this.OrderDetails = null;
        this.trackingDetails = [];
        this.actualTrackingDetails = [];
        if (this.trackingInput?.nativeElement && !selectedType) {
          this.trackingInput.nativeElement.value = '';
        }
      },
    });
  }

  DispatchDate(date: any): any {
    const statusDate = new Date(date);
    return statusDate.setHours(statusDate.getHours() - 1);
  }

  toggleDetails(id: string): void {
    this.isDisplayTrackingDetails = this.isDisplayTrackingDetails ? false : true;
    if (this.isDisplayTrackingDetails) { document.getElementById('gotoTop').focus({ preventScroll: false }); }
  }

  onSelectNumber(event: string): any {
    this.selectedType = event;
    this.placeholder = (event === 'order_number') ? 'Enter order number' : 'Enter tracking number';
  }

  updateStatus(): void {
    let latestStatus = '';

    if (this.OrderDetails.isFixTrackingNumber === true) {

      // First check actual tracking details
      if (this.actualTrackingDetails.length > 0) {
        if (this.OrderDetails.carrier_name === 'Tirupati') {
          this.latestTracking = this.actualTrackingDetails.reduce((latest, current) => {
            const latestDateTime = new Date(
              `${latest.date.split('T')[0]} ${latest.time}`
            );

            const currentDateTime = new Date(
              `${current.date.split('T')[0]} ${current.time}`
            );

            return currentDateTime > latestDateTime ? current : latest;
          });
        } else {
          this.latestTracking = this.actualTrackingDetails.reduce((latest, current) =>
            new Date(current.date).getTime() > new Date(latest.date).getTime()
              ? current
              : latest
          );
        }
        const formattedDate = format(
          new Date(this.latestTracking.date),
          'yyyy-MM-dd'
        );
        latestStatus = `${this.latestTracking.status}\n${formattedDate}`;
      }
      // Fallback to tracking details
      else if (this.trackingDetails.length > 0) {
        const dateObject = new Date(this.trackingDetails[0].date);
        const formattedDate = format(dateObject, 'yyyy-MM-dd');
        latestStatus = this.trackingDetails[0].status + '\n' + formattedDate;
      }

    } else {

      if (
        this.trackingDetails.length > 0 &&
        this.trackingDetails[0].status === 'Order Delivered'
      ) {
        const dateObject = new Date(this.trackingDetails[0].date);
        const formattedDate = format(dateObject, 'yyyy-MM-dd');
        this.latestTracking = this.trackingDetails[0];
        latestStatus = this.trackingDetails[0].status + '\n' + formattedDate;
      }
      else if (this.actualTrackingDetails.length > 0) {
        this.latestTracking = this.actualTrackingDetails.reduce((latest, current) =>
          new Date(current.date).getTime() > new Date(latest.date).getTime()
            ? current
            : latest
        );

        const formattedDate = format(
          new Date(this.latestTracking.date),
          'yyyy-MM-dd'
        );

        latestStatus = `${this.latestTracking.status}\n${formattedDate}`;
      }
      else if (this.trackingDetails.length > 0) {
        const dateObject = new Date(this.trackingDetails[0].date);
        const formattedDate = format(dateObject, 'yyyy-MM-dd');
        latestStatus = this.trackingDetails[0].status + '\n' + formattedDate;
      }

    }

    setTimeout(() => {
      const deliverstatusInput: HTMLInputElement = document.getElementById('deliverstatus') as HTMLInputElement;
      if (deliverstatusInput) {
        deliverstatusInput.value = latestStatus || 'Pending';
      }
    }, 300);
  }

  updateProgressBarWidth(): void {
    if (this.trackingDetails.length > 0) {
      const totalStatuses = 10;
      let latestStatusIndex = this.trackingDetails[0].status_index > totalStatuses ? 10 : this.trackingDetails[0].status_index;
      if (this.OrderDetails.isShipped === true) {
        latestStatusIndex = 10;
      }
      const progressWidthPercentage = (latestStatusIndex / totalStatuses) * 100;
      this.progressWidth = progressWidthPercentage;
    }
  }

  getSlug(cName): any {
    const cSlug = this.carrierCompanySlug.find(ele => ele.name === cName);
    return cSlug ? cSlug.slug : '';
  }

  getRatingWidth(rating: number): number {
    const normalizedRating = Math.max(0, Math.min(5, Number(rating || 0)));
    return (normalizedRating / 5) * 100;
  }

  private mapScrapedProduct(item: any): ProductAdvertisement | null {
    const product = item?.data?.data?.[0];

    if (!product) {
      return null;
    }

    const originalPriceValue = Number(product.mrp_price || product.order_price || product.price || 0);
    const priceValue = Number(product.price || product.order_price || 0);
    const stock = Number(product.stock || 0);

    return {
      title: product.title || 'Featured Product',
      rating: Number(product.fallback_ratings || 0),
      originalPrice: this.formatCurrency(originalPriceValue),
      price: this.formatCurrency(priceValue),
      discount: `${Number(product.discount_percentage || 0)}% OFF`,
      imageUrl: product.images?.[0]?.large?.url || product.images?.[0]?.small?.url || '',
      category: product.product_category || product.permalink || 'Trending Pick',
      cta: stock > 0 ? 'Shop Now' : 'Notify Me',
      stockLabel: stock > 0 ? `Only ${stock} left` : 'Sold out fast',
      url: item?.url || product.amazon_url || '#',
      badge: stock > 0 ? 'Hot Deal' : 'Popular Pick',
      taxonomy_title: product.taxonomy_title || product.product_category || 'Category',
      sales: product.sales ? `${product.sales} sold` : '',
      stock: stock
    };
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(value || 0);
  }

  refreshUpdateTrackingAccess(): void {
    const loggedInEmail = this._localService.getLoggedInEmail();
    this.canUpdateTracking =
      this._localService.hasAuthToken() &&
      loggedInEmail === Constants.trackingUpdateAllowedEmail;
    this._changeDetectorRef.markForCheck();
  }

  openUpdateTrackingModal(): void {
    if (!this.canUpdateTracking) {
      return;
    }
    this.updateTrackingForm.reset({
      shippingBy: this.OrderDetails?.carrier_name || '',
      trackingNumber: this.OrderDetails?.carrier_tracking_number || '',
    });
    this.showUpdateTrackingModal = true;
    this._changeDetectorRef.markForCheck();
  }

  closeUpdateTrackingModal(): void {
    if (this.isUpdatingTracking) {
      return;
    }
    this.showUpdateTrackingModal = false;
    this.updateTrackingForm.markAsPristine();
    this._changeDetectorRef.markForCheck();
  }

  submitUpdateTracking(): void {
    if (!this.canUpdateTracking) {
      this.toaster.error('You are not authorized to update tracking information.', '');
      this._changeDetectorRef.markForCheck();
      return;
    }
    if (this.updateTrackingForm.invalid) {
      this.updateTrackingForm.markAllAsTouched();
      this._changeDetectorRef.markForCheck();
      return;
    }

    const { shippingBy, trackingNumber } = this.updateTrackingForm.getRawValue();
    if (!shippingBy?.trim() || !trackingNumber?.trim()) {
      this.updateTrackingForm.markAllAsTouched();
      this._changeDetectorRef.markForCheck();
      return;
    }

    this.isUpdatingTracking = true;
    this._changeDetectorRef.markForCheck();
    this._trackingOrderService.updateTrackingInformation({
      tracking_number: this.OrderDetails.tracking_number,
      shippingBy: shippingBy.trim(),
      trackingNumber: trackingNumber.trim(),
    }).subscribe({
      next: (response: any) => {
        this.isUpdatingTracking = false;
        this.showUpdateTrackingModal = false;
        this.toaster.success(
          response?.message || 'Tracking information updated successfully.',
          ''
        );
        this.getOrderTrackingDetails(this.OrderDetails.tracking_number, 'tracking_number');
        this._changeDetectorRef.markForCheck();
      },
      error: ({ error }: { error: any }) => {
        this.isUpdatingTracking = false;
        this.toaster.error(error?.message || 'Failed to update tracking information.', '');
        this._changeDetectorRef.markForCheck();
      },
    });
  }
}
